'use client'

import { useEffect, useState } from 'react'
import { Divider, Label, Cell, Checkbox, Note, Icon, Card, Text } from '@vinted/web-ui'
import { Pencil24, Plus24 } from '@vinted/monochrome-icons'

import AddressDetails from 'components/AddressDetails'
import { useTranslate } from '@marketplace-web/shared/i18n'

import { AddressModel } from 'types/models/address'

import { ReturnAddressModal, BusinessAddressModal } from './Modals'

enum Field {
  BusinessAddress = 'businessAddress',
  ReturnAddress = 'returnAddress',
  UseSameAddress = 'useSameAddress',
}

type BusinessAddressesProps = {
  businessAddress: AddressModel | null
  businessAccountReturnAddress?: AddressModel | null
  businessAccountIsUsingTheSameAddress?: boolean
  isReturnAddressErrorShown: boolean
  onChangeReturnAddressErrorShow: (showError: boolean) => void
}

const BusinessAddresses = ({
  businessAddress,
  businessAccountReturnAddress,
  businessAccountIsUsingTheSameAddress = true,
  isReturnAddressErrorShown,
  onChangeReturnAddressErrorShow,
}: BusinessAddressesProps) => {
  const translate = useTranslate('profile_settings.business_addresses')
  const [showBusinessAddressModal, setShowBusinessAddressModal] = useState(false)
  const [showReturnAddressModal, setShowReturnAddressModal] = useState(false)
  const [isUsingTheSameAddress, setIsUsingTheSameAddress] = useState(
    businessAccountIsUsingTheSameAddress,
  )
  const [returnAddress, setReturnAddress] = useState(businessAccountReturnAddress || null)

  useEffect(() => {
    setIsUsingTheSameAddress(businessAccountIsUsingTheSameAddress)
  }, [businessAccountIsUsingTheSameAddress])

  function toggleBusinessAddressModal() {
    setShowBusinessAddressModal(!showBusinessAddressModal)
  }

  function toggleReturnAddressModal() {
    setShowReturnAddressModal(!showReturnAddressModal)
  }

  function changeReturnAddress(newReturnAddress: AddressModel | null) {
    setReturnAddress(newReturnAddress)
  }

  function handleAddressDetailsClick(type: Field) {
    return () => {
      switch (type) {
        case Field.BusinessAddress:
          toggleBusinessAddressModal()
          break
        case Field.ReturnAddress:
          toggleReturnAddressModal()
          break
        default:
          break
      }
    }
  }

  function toggleUsingTheSameAddress() {
    setIsUsingTheSameAddress(!isUsingTheSameAddress)
  }

  function renderAddress(address: AddressModel, type: Field) {
    return (
      <AddressDetails
        address={address}
        styling={Cell.Styling.Wide}
        type={Cell.Type.Navigating}
        suffix={<Icon name={Pencil24} color={Icon.Color.GreyscaleLevel2} />}
        onClick={handleAddressDetailsClick(type)}
      />
    )
  }

  function renderBusinessAddress() {
    if (!businessAddress) return null

    return (
      <>
        <Cell styling={Cell.Styling.Wide}>
          <Text
            as="h4"
            width={Text.Width.Parent}
            alignment={Text.Alignment.Left}
            type={Text.Type.Subtitle}
            text={translate('business_address.title')}
          />
        </Cell>
        {renderAddress(businessAddress, Field.BusinessAddress)}
        <BusinessAddressModal
          businessAddress={businessAddress}
          returnAddress={returnAddress}
          isBusinessAddressModalOpen={showBusinessAddressModal}
          onToggleBusinessAddressModal={toggleBusinessAddressModal}
        />
      </>
    )
  }

  function renderReturnAddressEdit() {
    if (isUsingTheSameAddress || !returnAddress) return null

    return (
      <>
        <Label
          styling={Label.Styling.Wide}
          type={Label.Type.Leading}
          text={translate('return_address.title')}
        />
        {renderAddress(returnAddress, Field.ReturnAddress)}
        <ReturnAddressModal
          isReturnAddressModalOpen={showReturnAddressModal}
          onToggleReturnAddressModal={toggleReturnAddressModal}
          onChangeReturnAddress={changeReturnAddress}
          onToggleUsingTheSameAddress={toggleUsingTheSameAddress}
          onChangeReturnAddressErrorShow={onChangeReturnAddressErrorShow}
          returnAddress={returnAddress}
          businessAddress={businessAddress}
        />
      </>
    )
  }

  function renderReturnAddressAdd() {
    if (isUsingTheSameAddress || returnAddress) return null

    return (
      <>
        <Divider />
        <Cell
          styling={Cell.Styling.Wide}
          type={Cell.Type.Navigating}
          title={translate('return_address.add_return_address')}
          suffix={<Icon name={Plus24} />}
          onClick={toggleReturnAddressModal}
          validation={
            isReturnAddressErrorShown &&
            translate('return_address.add_return_address_error_required')
          }
          clickable
        />
        <ReturnAddressModal
          isReturnAddressModalOpen={showReturnAddressModal}
          onToggleReturnAddressModal={toggleReturnAddressModal}
          onChangeReturnAddress={changeReturnAddress}
          onToggleUsingTheSameAddress={toggleUsingTheSameAddress}
          onChangeReturnAddressErrorShow={onChangeReturnAddressErrorShow}
          returnAddress={returnAddress}
          businessAddress={businessAddress}
        />
      </>
    )
  }

  function renderUseSameAddress() {
    if (returnAddress) return null

    return (
      <Cell
        styling={Cell.Styling.Wide}
        title={translate('use_same_address')}
        suffix={
          <Checkbox
            name={Field.UseSameAddress}
            onChange={toggleUsingTheSameAddress}
            checked={isUsingTheSameAddress}
          />
        }
      />
    )
  }

  return (
    <>
      <Card>
        {renderBusinessAddress()}
        {renderUseSameAddress()}
        {renderReturnAddressEdit()}
        {renderReturnAddressAdd()}
      </Card>
      <Note text={translate('note')} />
    </>
  )
}

export default BusinessAddresses
